<template>
  <div>
    <HpsfirProPageHeader
      title="二维码管理"
      title-en="QRCODE"
      description="批量生成小程序码并分发给驿站；生成时可不指定驿站，由驿站自行绑定"
      :top-operates="topOperates"
    />

    <HpsfirProTable
      ref="tableRef"
      :paginations="{ pageSize: 10 }"
      selection
      :on-selected="onSelected"
      :batch-operates="batchOperates"
      :columns="columns"
      :get-data="getData"
      :on-delete="deleteFun"
      :loading="loading"
      :is-dangder-delete="true"
      :operates="operates"
      :hide-export="true"
      :format-dangder-delete-info="formatDeleteInfo"
    />

    <!-- 批量生成：所属驿站可不选（生成无主二维码，驿站自行认领） -->
    <HpsfirProForm
      ref="batchFormRef"
      :items="batchItems"
      :is-dialog="true"
      title="批量生成二维码"
      description="不选驿站时生成的是未绑定二维码，发给驿站后由其在小程序内扫码绑定"
      :on-ok="submitBatch"
      text-ok="生成"
    />
  </div>

  <!-- 自管确认弹窗（原因见脚本里 confirmAsk 的注释） -->
  <HpsfirDialog :open="ask.open" @update:open="onAskOpenChange">
    <HpsfirDialogContent class="qr-confirm">
      <HpsfirDialogHeader>
        <HpsfirDialogTitle>{{ ask.title }}</HpsfirDialogTitle>
        <HpsfirDialogDescription>{{ ask.content }}</HpsfirDialogDescription>
      </HpsfirDialogHeader>
      <HpsfirDialogFooter class="qr-confirm__foot">
        <button class="qr-confirm__btn" type="button" @click="closeAsk(false)">取消</button>
        <button class="qr-confirm__btn qr-confirm__btn--primary" type="button" @click="closeAsk(true)">
          {{ ask.okText }}
        </button>
      </HpsfirDialogFooter>
    </HpsfirDialogContent>
  </HpsfirDialog>
</template>

<script lang="ts" setup>

/**
 * 二维码管理（hpsfir-managebase-ui 1.0.0）。
 *
 * 要点：
 *   - 批量生成时「所属驿站」可不选 —— 生成无主二维码（stationId 为空），
 *     把码发给驿站，驿站在小程序「我的驿站 → 绑定二维码」扫码/手输 ID 认领；
 *   - 列表里无主码的驿站列显示「未绑定」；
 *   - 导出走 topOperates 提供的按钮，**带上 ProTable 当前的查询条件**，
 *     与表格里看到的结果同口径（条件查 1 条就只导出 1 条）；
 *     不筛驿站时导出含无主码（未绑定驿站的码）。
 */
import { computed, h, onMounted, ref } from 'vue';
// 注意：Hpsfir* 组件一律**不要手动 import**。
// 框架会把组件按 __name 全局注册，并且**主题会用自己的实现再注册一遍**
// （registerUiComponents → registerProComponents → registerThemeComponents，
//  同名后者覆盖前者）。手动 import 拿到的是基座/默认主题那份，
// 会绕过主题覆盖 —— 页面要用主题组件就只能靠全局注册。
// 这里只 import 非组件的东西：函数（message）、组合式函数、以及类型。
import {
  message,
  type HpsfirProFormInstance,
  type HpsfirProFormItem,
  type HpsfirProTableColumn,
  type HpsfirProTableFilter,
  type HpsfirProTableInstance,
} from 'hpsfir-managebase-ui';
import { useApi } from '@/hooks/useApi';
import { absoluteUrl, renderQrImageCell, renderUrlSchemeCell } from '@/utils/qrcode-cell';

const api = useApi();
const loading = ref(false);
const exporting = ref(false);

const tableRef = ref<HpsfirProTableInstance>();
// 用框架导出的实例类型（组件不再手动 import，见文件顶部说明）
const batchFormRef = ref<HpsfirProFormInstance>();

const stationOptions = ref<{ value: number; label: string }[]>([]);

// ---------------- 列 ----------------
/**
 * 列宽说明（都是实测出来的，别随手改小）：
 *
 * - 固定布局：只要有一列声明了 width，整张表就是 `table-layout: fixed`
 *   （二维码图片列声明了 120，所以一直是固定布局）。此时未声明宽度的列
 *   均分剩余宽度，内容放不下也不会换行 —— 单元格是 white-space: nowrap、
 *   td 又是 overflow: visible，文字会直接盖到右邻列上。
 * - 声明 width 之后框架会给单元格加 `--width-limited`
 *   （td 转 white-space: normal、内容层 overflow-wrap: anywhere），内容才会在本列内换行。
 * - 列宽之和 + 操作列(302) + **框架多选列(40)** 就是表格的 min-width。
 *   1600 宽窗口下容器实测 1318px，所以数据列合计必须 ≤ 1318 − 302 − 40 = 976，
 *   否则会出现横向滚动条
 *   （曾经给 URL 列 281px 把表格撑到 1378 冒滚动条；加「备注」列时撑到 1472 又冒；
 *   改用框架 `selection` 多选列后多出 40px，实测 1356 又冒了一次 ——
 *   每加一列/每引入一个固定列都要重算这笔账）。
 * - 当前数据列合计 962（备注96 + 二维码ID170 + 驿站116 + 二维码112 +
 *   URL200 + 扫码数48 + 来源72 + 创建时间148），留 14px 余量。
 * - 各列内容实测需求：二维码ID 170px / 驿站 116px / 二维码 112px /
 *   URL 单行 281px / 扫码数 41px / 来源 72px / 创建时间 149px（均已含内边距）。
 *   URL 列因此取 200px —— 长链接在本列内换行，换来整张表不横向滚动。
 */
/**
 * 「驿站」单元格：未绑定驿站的二维码（无主码）显示「未绑定」，
 * 便于核对待分发的二维码有哪些还没被驿站认领。
 */
function renderStationName(scope: any) {
  return scope?.row?.stationName ? scope.row.stationName : '未绑定';
}

// ---------------- 选中状态（框架 selection 事件回填，批量操作用） ----------------
const selectedRows = ref<any[]>([]);
const batchRunning = ref(false);

/**
 * 框架多选回调：勾选/取消勾选都会把**当前全部选中行**回传，直接覆盖即可。
 *
 * 说明：这里依赖框架 `selection` + `HpsfirCheckbox` 的选中态契约。
 * 框架 1.0.0 曾因 `HpsfirCheckbox` 只声明 `modelValue`/`update:modelValue`
 * 而内部按 `checked`/`update:checked` 使用，导致本回调从不触发；已在框架侧修复
 * （HpsfirCheckbox 同时兼容两种写法）。
 */
const onSelected = (rows: any[]) => {
  selectedRows.value = Array.isArray(rows) ? rows : [];
};

/**
 * 重新生成 NFC 链接（**不动小程序码图片**）。
/**
 * 重新生成 NFC 链接（**不动小程序码图片**）。
/**
 * 重新生成 NFC 链接（**不动小程序码图片**）。
 *
 * 为什么不复用行里的「更新链接」：那个走 `qrcode/refresh`，会把小程序码图片
 * 也重新拉一遍 —— 几百枚批量补 NFC 时白白多花 0.9s/枚 和微信调用额度，
 * 而图片本来就没变。这里只补 `urlScheme`。
 */
const refreshNfc = async(rows: any[]) => {
  const ids = rows.map((row) => Number(row?.id)).filter((id) => Boolean(id));
  if (!ids.length) {
    return;
  }
  batchRunning.value = true;
  try {
    const res: any = await api.searchTrackAdminApi.qrcodeRefreshNfcSearchTrackAdmin({
      batchIdsVo: { ids },
    } as any);
    const payload = res?.data;
    if (payload?.success) {
      message.success(payload.msg || '已重新生成');
      selectedRows.value = [];
      tableRef.value?.refresh();
      return;
    }
    // 未配置设备参数时后端返回一段明确原因，原样透出（便于运营知道要等审核/去填配置）
    message.error(payload?.msg || '重新生成失败');
  } finally {
    batchRunning.value = false;
  }
};

/**
 * 自管确认弹窗。
 *
 * ⚠️ 为什么不用框架的 `confirm()`：本版本里点它的「确定」按钮只会关掉弹窗，
 *   promise **既不 resolve 也不 reject**（实测：对话框消失、无请求、无报错、
 *   后续代码永远不执行）—— 表现为「点了确定没反应」。
 *   这里用基础 `HpsfirDialog` + 普通按钮自己接管，行为完全可控。
 */
const ask = ref({ open: false, title: '', content: '', okText: '确定' });
let askResolve: ((ok: boolean) => void) | null = null;

/** 弹确认框，返回用户是否点了确定（Esc / 点遮罩 都算取消） */
const confirmAsk = (title: string, content: string, okText = '确定') => new Promise<boolean>((resolve) => {
  askResolve = resolve;
  ask.value = { open: true, title, content, okText };
});

const closeAsk = (ok: boolean) => {
  ask.value = { ...ask.value, open: false };
  askResolve?.(ok);
  askResolve = null;
};

const onAskOpenChange = (open: boolean) => {
  if (!open) {
    closeAsk(false);
  }
};

const refreshNfcLink = async(row: any) => {
  const ok = await confirmAsk(
    '重新生成 NFC 链接',
    `将重新生成二维码 ${row?.code || ''} 的 NFC 链接（小程序码图片不变）。`
    // 微信侧「一机一码」：SN(=码的 code) 与链接**永久绑定**，重复生成只会取回原链接，
    // 所以旧标签不会失效（早先写的「旧链接会失效」是错的，会误导用户去重写标签）。
    + '链接与二维码固定绑定，重新生成只补齐尚未生成的链接，已写入标签的链接不受影响。是否继续？',
  );
  if (!ok) {
    return;
  }
  await refreshNfc([row]);
};


/* eslint-disable max-lines-per-function -- 列定义是一个大数组字面量：逐列拆成
   多个函数反而更难对照（每列的行宽/筛选开关要横向比较才有意义） */
const columns = computed<HpsfirProTableColumn[]>(() => [
  {
    // 备注放第一列：管理端靠它区分批次/用途，关键字搜索也覆盖这一列（后端 listQrcodes）
    name: 'remark',
    label: '备注',
    type: 'string',
    isCondition: true,
    hideCreate: true,
    hideEdit: true,
    width: 96,
  },
  {
    name: 'code',
    label: '二维码ID',
    type: 'string',
    isCondition: true,
    hideCreate: true,
    hideEdit: true,
    width: 170,
  },
  {
    name: 'stationName',
    label: '驿站',
    type: 'select',
    options: stationOptions.value,
    isCondition: true,
    hideCreate: true,
    hideEdit: true,
    width: 116,
    formRender: renderStationName,
    render: renderStationName,
  },
  {
    name: 'qrImageUrl',
    label: '二维码',
    type: 'string',
    hideCreate: true,
    hideEdit: true,
    width: 112,
    render: (scope: any) => renderQrImageCell(scope, api.baseUrl),
  },
  {
    name: 'urlScheme',
    label: 'NFC 链接',
    type: 'string',
    hideCreate: true,
    hideEdit: true,
    width: 200,
    // 单元格里的「重新生成」只补 NFC 链接，不重取小程序码图片
    render: (scope: any) => renderUrlSchemeCell(scope, { onRefresh: refreshNfcLink }),
  },
  {
    name: 'scanCount',
    label: '扫码数',
    type: 'string',
    hideCreate: true,
    hideEdit: true,
    width: 48,
  },
  {
    name: 'source',
    label: '来源',
    type: 'string',
    hideCreate: true,
    hideEdit: true,
    width: 72,
  },
  {
    name: 'createTime',
    label: '创建时间',
    type: 'string',
    hideCreate: true,
    hideEdit: true,
    width: 148,
  },
]);
/* eslint-enable max-lines-per-function */

const formatDeleteInfo = (row: any) => `您正在进行删除二维码（${row?.code || ''}）操作，请谨慎操作`;

// ---------------- 行内操作 ----------------
const refreshLink = async(row: any) => {
  const res: any = await api.searchTrackAdminApi.qrcodeRefreshSearchTrackAdmin({
    refreshVo: { id: row.id },
  } as any);
  if (res?.data?.success) {
    message.success('已更新为新的永久链接');
    tableRef.value?.refresh();
  } else {
    message.error(res?.data?.msg || '更新失败');
  }
};

const downloadImage = (row: any) => {
  if (!row?.qrImageUrl) {
    message.error('该二维码暂无图片');
    return;
  }
  const link = document.createElement('a');
  link.href = absoluteUrl(api.baseUrl, row.qrImageUrl);
  link.download = `${row.code || 'qrcode'}.png`;
  link.target = '_blank';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

const operates = [
  { key: 'refresh', name: '更新链接', handler: refreshLink },
  { key: 'download', name: '下载图片', handler: downloadImage },
];


// ---------------- 批量操作（框架 batch-operates：勾选后出现在表头） ----------------
const batchRefreshNfc = async(rows: any[]) => {
  if (!rows?.length) {
    return;
  }
  const ok = await confirmAsk(
    '批量重新生成 NFC 链接',
    `将重新生成选中的 ${rows.length} 枚二维码的 NFC 链接（小程序码图片不变）。`
    + '链接与二维码固定绑定，重新生成只补齐尚未生成的链接，已写入标签的链接不受影响。是否继续？',
  );
  if (!ok) {
    return;
  }
  await refreshNfc(rows);
};

const batchDelete = async(rows: any[]) => {
  if (!rows?.length) {
    return;
  }
  const ok = await confirmAsk(
    '批量删除二维码',
    `您正在进行批量删除二维码操作，共 ${rows.length} 枚，删除后无法恢复。请谨慎操作`,
    '确定删除',
  );
  if (!ok) {
    return;
  }
  const ids = rows.map((row) => Number(row?.id)).filter((id) => Boolean(id));
  batchRunning.value = true;
  try {
    const res: any = await api.searchTrackAdminApi.qrcodeBatchDeleteSearchTrackAdmin({
      batchIdsVo: { ids },
    } as any);
    const payload = res?.data;
    if (payload?.success) {
      message.success(payload.msg || '删除成功');
      selectedRows.value = [];
      tableRef.value?.refresh();
      return;
    }
    message.error(payload?.msg || '删除失败');
  } finally {
    batchRunning.value = false;
  }
};

/**
 * 框架 `batch-operates`：勾选行后展示的批量入口。
 * `handler` 直接收到**当前选中的行数组**（框架内部把 selects 传进来）。
 */
const batchOperates = [
  {
    key: 'batchRefreshNfc',
    name: '批量重新生成 NFC 链接',
    handler: (rows: any[]) => batchRefreshNfc(rows),
  },
  {
    key: 'batchDelete',
    name: '批量删除',
    handler: (rows: any[]) => batchDelete(rows),
  },
];


// ---------------- 批量生成 ----------------
const batchItems = computed<HpsfirProFormItem[]>(() => [
  {
    name: 'stationId',
    label: '所属驿站',
    type: 'select',
    options: stationOptions.value,
    clearable: true,
    filterable: true,
    placeholder: '不选则由驿站自行绑定',
  },
  {
    name: 'count',
    label: '生成数量',
    type: 'number',
    min: 1,
    max: 500,
    rule: [{ required: true, message: '请输入生成数量' }],
  },
  {
    name: 'remark',
    label: '备注',
    type: 'string',
  },
]);

const openBatch = () => {
  // 每次打开都从「未选驿站」开始，避免上一次的筛选/输入被带进来
  batchFormRef.value?.reset();
  batchFormRef.value?.showDialog();
};

const submitBatch = async(data: any) => {
  const res: any = await api.searchTrackAdminApi.qrcodeBatchGenerateSearchTrackAdmin({
    batchVo: {
      stationId: data.stationId || undefined,
      count: Number(data.count) || 1,
      remark: data.remark || undefined,
    },
  } as any);
  if (res?.data?.success) {
    const count = (res.data.data?.length ?? Number(data.count)) || 0;
    message.success(`已生成 ${count} 个二维码`);
    tableRef.value?.refresh();
  } else {
    throw new Error(res?.data?.msg || '生成失败');
  }
};

/**
 * 读取 ProTable **当前生效的查询条件**。
 *
 * 框架把条件存在暴露出来的 `filter` ref 上：`handleSearch` / `handleConditionChange`
 * 都往 `filter.value.conditions` 里写（即 `getData` 收到的那一份），
 * 所以读它就等于读「表格当前的筛选」，无需自己再维护一份状态。
 *
 * 兼容两种形态：暴露对象可能是 ref 本身，也可能被 proxyRefs 解包过。
 */
const currentConditions = (): Record<string, any> => {
  const filter: any = (tableRef.value as any)?.filter;
  // 暴露对象可能是 ref 本身（`.value.conditions`），也可能被 proxyRefs 解包过（`.conditions`）
  const value = filter?.value === undefined ? filter : filter.value;
  return value?.conditions || {};
};

/**
 * 导出 Excel：**必须带上当前查询条件**，导出与查询结果一致。
 * 条件映射与 `getData` 完全一致（同一组 keyword / stationId），
 * 否则会出现「界面 1 条、导出全量」的口径偏差。
 */
const exportExcel = async() => {
  exporting.value = true;
  try {
    const conditions = currentConditions();
    // 条件映射与 getData 完全一致，否则会出现「界面 1 条、导出全量」的口径偏差。
    // `remark` 是快速搜索框绑定的列（搜备注），`code` 来自高级筛选；
    // 只填一处时用它当通用关键字，两处都填时另一个走 remark 参数。
    const code = conditions.code || undefined;
    const quick = conditions.remark || undefined;
    const keyword = code || quick;
    const remark = code && quick ? quick : undefined;
    const stationId = conditions.stationName || conditions.stationId || undefined;
    const res: any = await api.searchTrackAdminApi.qrcodeExportSearchTrackAdmin(keyword, remark, stationId);
    const url = res?.data?.data;
    if (!res?.data?.success || !url) {
      message.error(res?.data?.msg || '导出失败');
      return;
    }
    window.open(absoluteUrl(api.baseUrl, url), '_blank');
  } finally {
    exporting.value = false;
  }
};

// 导出进行中禁用按钮，避免连点重复下载
const topOperates = computed(() => [
  { key: 'batch', name: '批量生成', handler: openBatch, variant: 'default' as const },
  {
    key: 'export',
    name: exporting.value ? '导出中…' : '导出 Excel',
    handler: exportExcel,
    variant: 'outline' as const,
    disabled: exporting.value,
  },
]);

// ---------------- 数据 ----------------
const loadStations = async() => {
  const res: any = await api.searchTrackAdminApi.stationListSearchTrackAdmin();
  const rows = res?.data?.data || [];
  stationOptions.value = rows.map((item: any) => ({ value: item.id, label: item.name }));
};

const getData = async(filter: HpsfirProTableFilter) => {
  loading.value = true;
  try {
    // 快速搜索框绑的是「第一个文本型条件列」= 备注（列名 remark），
    // 高级筛选给的是「二维码ID」= code。
    //
    // 快速搜索的值按**通用关键字**下发（后端 keyword 会同时匹配
    // 二维码ID / 驿站名 / 备注 / 明文链接）—— 这样在这个框里输
    // 二维码ID 或 备注 都能搜到，不会因为「框改成搜备注」而搜不到 ID。
    // 两个条件同时存在时（高级筛选填了 ID + 快速搜索填了备注），
    // 剩下那个走 remark 参数，后端取交集。
    const code = filter.conditions?.code || undefined;
    const quick = filter.conditions?.remark || undefined;
    const keyword = code || quick;
    const remark = code && quick ? quick : undefined;
    const res: any = await api.searchTrackAdminApi.qrcodePageSearchTrackAdmin(
      keyword,
      remark,
      filter.conditions?.stationName || filter.conditions?.stationId || undefined,
      filter.paginations.page,
      filter.paginations.pageSize,
    );
    const rows = res?.data?.data?.data || [];
    // 记录当前页行，供「全选本页」用
    return {
      total: res?.data?.data?.total || 0,
      data: rows,
    };
  } finally {
    loading.value = false;
  }
};

const deleteFun = async(data: any) => {
  const res: any = await api.searchTrackAdminApi.qrcodeDeleteSearchTrackAdmin(data.id);
  if (res?.data?.success) {
    message.success('删除成功');
  } else {
    throw new Error(res?.data?.msg || '删除失败');
  }
};

onMounted(loadStations);
</script>

<!--
  注意：这里**不能用 scoped**。render 返回的 VNode 是由 ProTable（父组件）渲染的，
  作用域 ID 属于表格组件而不是本页面，scoped CSS 匹配不到这些元素
  （踩过这个坑：复制按钮量出来 24px、font-size 也没生效，因为样式压根没命中）。
  为此所有类名都加 qr- 前缀，避免全局样式外溢。
-->
